// The Anchor Loop seams Jabberr is one side of (docs/adr/OPEN-34, OPEN-36): saving to the Anchor Workbench, and tagging
// model calls for the Compute gateway.
import { z } from 'zod';
import type { Audience, Modality, ModelRequestPurpose, TurnPurpose } from './domain.js';

// --- Save to the Workbench (OPEN-34) ---

/** The product-layer item kinds the Workbench accepts from Jabberr (anchor-loop contracts F). */
export const WorkbenchItemKind = z.enum(['requirement', 'decision', 'brief']);
export type WorkbenchItemKind = z.infer<typeof WorkbenchItemKind>;

/** The OPEN-21 document kinds that map one to one onto product-layer items; any other output is not saved. */
export function workbenchKindFor(purpose: TurnPurpose): WorkbenchItemKind | null {
  if (purpose === 'requirements') return 'requirement';
  if (purpose === 'decision') return 'decision';
  if (purpose === 'brief') return 'brief';
  return null;
}

/** A saved output's Workbench item: its number in the Workbench's one id space, and where to open it. */
export const WorkbenchRef = z.object({
  number: z.number().int().positive(),
  id: z.string().min(1),
  url: z.string().url(),
  savedAt: z.string(),
});
export type WorkbenchRef = z.infer<typeof WorkbenchRef>;

// POST /api/projects/:projectId/anchor-link: an owner links the project to its Anchor Yard record. The account and the
// Workbench project come from Yard, never from the owner: the owner may only pick one of the Workbench projects Yard
// binds to this project (Raven's Retreat has two).
export const LinkAnchorRequest = z.object({ workbenchProjectId: z.string().regex(/^[a-z0-9][a-z0-9-]{0,79}$/).optional() });
export type LinkAnchorRequest = z.infer<typeof LinkAnchorRequest>;

// POST /api/projects/:projectId/outputs/:turnId/workbench -> the item, created once
export const SaveToWorkbenchResponse = z.object({ workbench: WorkbenchRef });
export type SaveToWorkbenchResponse = z.infer<typeof SaveToWorkbenchResponse>;

// --- Compute gateway tags (OPEN-36, anchor-loop contracts E) ---

/** What a Jabberr call is for, in the gateway's terms (the RunRecord v2 purposes Jabberr sends). */
export const AnchorPurpose = z.enum(['jabberr_chat', 'keen', 'room_bot']);
export type AnchorPurpose = z.infer<typeof AnchorPurpose>;

/**
 * Keen decisions are 'keen'; a spoken reply (the room bot and live meetings, modality voice) is 'room_bot'; every
 * other call (typed replies, the summary checkpoint, memory extraction) is 'jabberr_chat'.
 */
export function anchorPurposeFor(purpose: ModelRequestPurpose | undefined, modality: Modality): AnchorPurpose {
  if (purpose === 'decision') return 'keen';
  if ((purpose ?? 'reply') === 'reply' && modality === 'voice') return 'room_bot';
  return 'jabberr_chat';
}

/**
 * Whose text a token count is for (OPEN-36). Through the Compute gateway, /tokenize needs the same tags as a completion
 * and the credential of the text's audience: the api resolves anchor from projectId (services/anchor-tags.ts), and a
 * count with no resolvable tags is only estimated locally, never sent.
 */
export interface TokenScope {
  audience: Audience;
  projectId?: string;
  conversationId?: string;
  anchor?: AnchorTags;
}

/** Who a call is for, set by the api from the call's project and conversation (never from the model or the browser). */
export interface AnchorTags {
  /** The Anchor Yard account (acc_...), from Yard's project record. */
  accountId: string | null;
  /** Anchor Yard's project id (GET /api/projects/by-jabberr/:slug), never Jabberr's own id or slug. */
  project: string;
  /** 'client' when someone outside Tarawa is in the conversation (a guest's client-audience role, OPEN-27). */
  audience: Audience;
}
